<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div :class="isSmall ? 'small' : ''">
    <chart
      template-ref="unitAudienceData.audienceDataChart"
      :name="capitalize($t('messages.impressionsPerPlay'))"
      :mode="ChartMode.VERBOSE"
      :actions="chartActions"
      :echarts-options="audienceDataChartOptions"
      :theme="ChartTheme.ONE_TONE_BLUE" />

    <div class="week-nav">
      <button
        class="btn btn-default btn-xs"
        @click="previousWeek">
        &lt;&lt;
      </button>
      <span v-sanitize-html="weekDescription" />
      <button
        class="btn btn-default btn-xs"
        @click="nextWeek">
        &gt;&gt;
      </button>
    </div>

    <table
      class="table"
      :class="isSmall ? '' : 'table-bordered'">
      <colgroup>
        <col width="12.5%" />
        <col width="12.5%" />
        <col width="12.5%" />
        <col width="12.5%" />
        <col width="12.5%" />
        <col width="12.5%" />
        <col width="12.5%" />
        <col width="12.5%" />
      </colgroup>
      <thead>
        <tr>
          <th />
          <template
            v-for="(day, i) in days"
            :key="i">
            <th>
              <span>{{ isSmall ? day.labelTiny : day.date.toLocaleString(DateTime.DATE_FULL) }}</span>
            </th>
          </template>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(hour, hourIndex) in days[0].hours"
          :key="hourIndex">
          <th>{{ hour.label }}</th>
          <template
            v-for="(day, dayIndex) in days"
            :key="dayIndex">
            <td>
              <div
                class="cell"
                :style="`background-color:rgba(0,224,116,${weights[dayIndex * 24 + hourIndex]})`">
                {{ audienceDataFormatted[dayIndex * 24 + hourIndex] }}
              </div>
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, onMounted, ref, watch } from 'vue';

  import Chart from '@/common/components/chart/Chart.vue';
  import ChartDownloadGroupActionsUtils from '@/common/components/chart/chart-download-group-actions';
  import ChartFullscreenActions from '@/common/components/chart/chart-fullscreen-actions';
  import ChartIntervalGroupActionsUtils from '@/common/components/chart/chart-interval-group-actions';
  import ChartTimezoneGroupActionsUtils from '@/common/components/chart/chart-timezone-group-actions';
  import ChartTrendActionsUtils from '@/common/components/chart/chart-trend-actions';
  import ChartTypeGroupActions from '@/common/components/chart/chart-type-group-actions';
  import CellType from '@/common/enums/cell-type.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import ChartType from '@/common/enums/chart-type.enum';
  import TimeGranularity from '@/common/enums/time-granularity.enum';
  import TimeseriesAxisX from '@/common/models/chart/TimeseriesAxisX';
  import Day from '@/common/models/Day';
  import { DASH } from '@/globals';
  import formatNumber from '@/utils/NumberFormatter';
  import QuickMathUtils from '@/utils/QuickMathUtils';
  import i18n from '@/vueI18n';

  interface IProps {
    audienceData: number[];
    isSmall?: boolean;
    startDate: DateTime;
    audienceDataFormat?: string;
    chartTooltipName?: string;
  }

  const {
    audienceData,
    isSmall = false,
    startDate,
    audienceDataFormat = '0,0.00',
    chartTooltipName,
  } = defineProps<IProps>();

  interface IEmits {
    (e: 'previousWeek'): void;
    (e: 'nextWeek'): void;
  }

  const emits = defineEmits<IEmits>();

  const chartActions = ref([
    ChartTypeGroupActions.getActions(0, true, [ChartType.LINE, ChartType.BAR], ChartType.LINE),
    ChartTimezoneGroupActionsUtils.DefaultChartTimezoneGroupActions,
    ChartIntervalGroupActionsUtils.ChartIntervalGroupActions(
      2,
      false,
      Object.values(TimeGranularity),
      TimeGranularity.HOURLY,
      QuickMathUtils.sum,
    ),
    ChartFullscreenActions.getActions(1, false),
    ChartDownloadGroupActionsUtils.DefaultChartDownloadOptions,
    ChartTrendActionsUtils.DefaultChartTrendActions,
  ]);

  const weights = ref([]);

  const audienceDataChartOptions = computed(() => {
    const { userTz } = ChartTimezoneGroupActionsUtils;
    const xAxisTimeFormatter = (value, zone) => {
      const date = ChartTimezoneGroupActionsUtils.localizedDate(value, zone);
      return date.toLocaleString(DateTime.DATETIME_MED);
    };
    const hours = audienceData.map((x, i) => startDate.plus({ hours: i }).toMillis());
    const serieFormatter = (value) => formatNumber(value, audienceDataFormat);

    const options = {
      grid: {
        top: 65,
        bottom: 60,
        left: 60,
        right: 50,
      },
      calculable: true,
      xAxis: TimeseriesAxisX.fromObject({
        name: capitalize(i18n.global.t('names.date', { n: 1 })),
        data: hours.map((h) => xAxisTimeFormatter(h, userTz)),
        raw: hours,
        transform: xAxisTimeFormatter,
        granularity: TimeGranularity.HOURLY,
      }),
      yAxis: [
        {
          axisLabel: { show: true, formatter: (value) => serieFormatter(value) },
        },
      ],
      series: [
        {
          cellType: CellType.INTEGER,
          name: chartTooltipName,
          type: 'line',
          data: audienceData,
          smooth: true,
          showAllSymbol: true,
          transform: (value) => serieFormatter(value),
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          let series = `${params[0].axisValueLabel}<br />`;
          params.forEach((param) => {
            const { marker, seriesName } = param;
            if (param.value === undefined) return;
            const value = serieFormatter(param.value);
            series += `${marker} ${seriesName}: ${value}<br />`;
          });
          return series;
        },
      },
    };

    return options;
  });

  const audienceDataFormatted = computed(() => {
    return audienceData.map((x) => (x === 0 ? DASH : formatNumber(x, audienceDataFormat)));
  });

  const days = computed(() => {
    const days = [
      new Day(startDate),
      new Day(startDate.plus({ days: 1 })),
      new Day(startDate.plus({ days: 2 })),
      new Day(startDate.plus({ days: 3 })),
      new Day(startDate.plus({ days: 4 })),
      new Day(startDate.plus({ days: 5 })),
      new Day(startDate.plus({ days: 6 })),
    ];
    return days;
  });
  const weekDescription = computed(() => {
    const week = capitalize(i18n.global.t('names.week', { n: 1 }));
    return `<b>${week} ${startDate.weekNumber}:</b> ${startDate.toLocaleString(DateTime.DATE_FULL)} – ${startDate.plus({ days: 6 }).toLocaleString(DateTime.DATE_FULL)}`;
  });

  watch(
    () => audienceData,
    () => {
      updateWeights();
    },
  );

  function previousWeek() {
    emits('previousWeek');
  }

  function nextWeek() {
    emits('nextWeek');
  }

  function updateWeights() {
    const max = Math.max(...audienceData);
    weights.value = audienceData.map((x) => (max === 0 ? 0 : x / max));
  }

  onMounted(() => {
    updateWeights();
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  table {
    font-size: 0.9em;

    tbody {
      td {
        padding: 0;

        .cell {
          text-align: right;
          padding: 0.7rem;
        }
      }
    }
  }

  .week-nav {
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.6rem;
    border-top: solid 0.1rem $borders-light;
    border-bottom: solid 0.1rem $borders-light;
    padding: 1rem;

    span {
      flex-grow: 1;
      margin: auto 1.6rem;
      text-align: center;
    }

    button {
    }
  }

  .chart,
  .chart .echarts {
    width: 100%;
    height: 30rem;
    margin: 0;
  }
</style>
